<template>
  <v-dialog v-model="dialog" persistent max-width="960px" scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-list-item v-bind="attrs" v-on="on">
        <v-list-item-avatar tile>
          <v-img src="@/assets/AdminTools/new-product.png" />
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>Merge Product</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <v-card class="product-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Merge Products</div>
          <div class="dialog-subtitle">
            Move sales, purchases, inventory, and history into one product.
          </div>
        </div>
        <v-btn icon small aria-label="Close merge products" @click.stop.prevent="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body pa-4">
        <div class="merge-summary mb-4">
          <div class="summary-icon">
            <v-icon color="primary">mdi-source-merge</v-icon>
          </div>
          <div class="summary-copy">
            <div class="summary-title">Choose what moves and what remains</div>
            <div class="dialog-subtitle">
              The source product's records move to the target product. The target
              product name, pricing, and settings remain unchanged.
            </div>
          </div>
          <v-checkbox
            v-model="deleteFromProduct"
            label="Delete source after merge"
            color="primary"
            hide-details
            class="delete-source-check mt-0"
          />
        </div>

        <v-row dense align="stretch">
          <v-col cols="12" md="5">
            <div class="form-section product-section">
              <div class="section-heading">
                <div>
                  <div class="section-kicker">FROM</div>
                  <div class="section-title">Source product</div>
                </div>
                <v-chip x-small color="orange lighten-4" text-color="orange darken-3">
                  Records move out
                </v-chip>
              </div>

              <div class="field-label">Search source product</div>
              <v-autocomplete
                v-model="selectedFromProduct"
                :items="Products"
                :loading="loadingProducts"
                item-text="name"
                return-object
                clearable
                outlined
                dense
                hide-no-data
                hide-details
                prepend-inner-icon="mdi-magnify"
                label="Product name or ID"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.localId }} · {{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ categoryName(item) }} · {{ trackingLabel(item) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-chip x-small outlined>Stock {{ item.balance || 0 }}</v-chip>
                </template>
              </v-autocomplete>

              <div v-if="selectedFromProduct" class="selected-product-card mt-4">
                <div class="selected-product-header">
                  <div class="selected-product-name">{{ selectedFromProduct.name }}</div>
                  <v-chip x-small outlined>Product #{{ selectedFromProduct.localId || selectedFromProduct.id }}</v-chip>
                </div>
                <div class="product-detail-grid">
                  <div class="product-detail">
                    <span>Category</span>
                    <strong>{{ categoryName(selectedFromProduct) }}</strong>
                  </div>
                  <div class="product-detail">
                    <span>Available stock</span>
                    <strong>{{ selectedFromProduct.balance || 0 }}</strong>
                  </div>
                  <div class="product-detail">
                    <span>Inventory</span>
                    <strong>{{ selectedFromProduct.trackInventory ? "Tracked" : "Not tracked" }}</strong>
                  </div>
                  <div class="product-detail">
                    <span>Serial / IMEI</span>
                    <strong>{{ selectedFromProduct.trackSerialNumber ? "Tracked" : "Not tracked" }}</strong>
                  </div>
                </div>
              </div>
              <div v-else class="selection-placeholder mt-4">
                Select the duplicate product whose records should move.
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="2" class="merge-direction">
            <div class="direction-line" />
            <div class="direction-icon">
              <v-icon color="primary">mdi-arrow-right</v-icon>
            </div>
            <div class="direction-label">MERGE INTO</div>
            <div class="direction-line" />
          </v-col>

          <v-col cols="12" md="5">
            <div class="form-section product-section">
              <div class="section-heading">
                <div>
                  <div class="section-kicker">TO</div>
                  <div class="section-title">Target product</div>
                </div>
                <v-chip x-small color="green lighten-4" text-color="green darken-3">
                  Product remains
                </v-chip>
              </div>

              <div class="field-label">Search target product</div>
              <v-autocomplete
                v-model="selectedToProduct"
                :items="Products"
                :loading="loadingProducts"
                item-text="name"
                return-object
                clearable
                outlined
                dense
                hide-no-data
                hide-details
                prepend-inner-icon="mdi-magnify"
                label="Product name or ID"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.localId }} · {{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ categoryName(item) }} · {{ trackingLabel(item) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-chip x-small outlined>Stock {{ item.balance || 0 }}</v-chip>
                </template>
              </v-autocomplete>

              <div v-if="selectedToProduct" class="selected-product-card mt-4">
                <div class="selected-product-header">
                  <div class="selected-product-name">{{ selectedToProduct.name }}</div>
                  <v-chip x-small outlined>Product #{{ selectedToProduct.localId || selectedToProduct.id }}</v-chip>
                </div>
                <div class="product-detail-grid">
                  <div class="product-detail">
                    <span>Category</span>
                    <strong>{{ categoryName(selectedToProduct) }}</strong>
                  </div>
                  <div class="product-detail">
                    <span>Available stock</span>
                    <strong>{{ selectedToProduct.balance || 0 }}</strong>
                  </div>
                  <div class="product-detail">
                    <span>Inventory</span>
                    <strong>{{ selectedToProduct.trackInventory ? "Tracked" : "Not tracked" }}</strong>
                  </div>
                  <div class="product-detail">
                    <span>Serial / IMEI</span>
                    <strong>{{ selectedToProduct.trackSerialNumber ? "Tracked" : "Not tracked" }}</strong>
                  </div>
                </div>
              </div>
              <div v-else class="selection-placeholder mt-4">
                Select the product that should remain after the merge.
              </div>
            </div>
          </v-col>
        </v-row>

        <v-alert
          v-if="selectionIssues.length"
          type="warning"
          text
          dense
          class="merge-warning mb-0 mt-2"
        >
          <div class="font-weight-bold mb-1">These products cannot be merged yet:</div>
          <div v-for="issue in selectionIssues" :key="issue">{{ issue }}</div>
        </v-alert>
      </v-card-text>

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn color="primary" text @click="close">Close</v-btn>
        <v-btn
          color="primary"
          rounded
          depressed
          :loading="loading"
          :disabled="!canMerge"
          @click="submit"
        >
          <v-icon left small>mdi-source-merge</v-icon>
          Merge Products
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import productService from "../service.js";

export default {
  name: "merge-product",
  data() {
    return {
      deleteFromProduct: true,
      selectedFromProduct: null,
      selectedToProduct: null,
      loading: false,
      loadingProducts: false,
      dialog: false,
      Products: [],
    };
  },
  computed: {
    selectionIssues() {
      const source = this.selectedFromProduct;
      const target = this.selectedToProduct;
      if (!source || !target) return [];

      const issues = [];
      if (Number(source.id) === Number(target.id)) {
        issues.push("Choose two different products.");
      }
      if (
        Boolean(source.active) &&
        Boolean(source.active) !== Boolean(target.active)
      ) {
        issues.push("Both products must be active.");
      }
      if (Boolean(source.trackInventory) !== Boolean(target.trackInventory)) {
        issues.push("Inventory tracking must match on both products.");
      }
      if (
        Boolean(source.trackSerialNumber) !== Boolean(target.trackSerialNumber)
      ) {
        issues.push("Serial / IMEI tracking must match on both products.");
      }
      if (Number(source.CategoryId || 0) !== Number(target.CategoryId || 0)) {
        issues.push("Both products must be in the same category.");
      }
      return issues;
    },
    canMerge() {
      return Boolean(
        this.selectedFromProduct &&
          this.selectedToProduct &&
          this.selectionIssues.length === 0 &&
          !this.loading
      );
    },
  },
  watch: {
    dialog(isOpen) {
      if (isOpen) this.getProduct();
    },
  },
  methods: {
    categoryName(product) {
      return product && product.Category && product.Category.name
        ? product.Category.name
        : "Category unavailable";
    },
    trackingLabel(product) {
      if (product.trackSerialNumber) return "Serial tracked";
      if (product.trackInventory) return "Quantity tracked";
      return "Inventory not tracked";
    },
    getProduct() {
      this.loadingProducts = true;
      return productService
        .getAll({ limit: 5000 })
        .then((result) => {
          this.Products = Array.isArray(result.data) ? result.data : [];
        })
        .catch((error) => {
          const message =
            error?.data?.message ||
            error?.response?.data?.message ||
            "Unable to load products.";
          this.$swal("Merge Products", message, "error");
        })
        .finally(() => {
          this.loadingProducts = false;
        });
    },
    close() {
      this.empty();
      this.dialog = false;
    },
    empty() {
      this.selectedFromProduct = null;
      this.selectedToProduct = null;
      this.deleteFromProduct = true;
    },
    async submit() {
      if (!this.canMerge) return;

      const source = this.selectedFromProduct;
      const target = this.selectedToProduct;
      const confirmation = await this.$swal({
        title: "Merge these products?",
        text: `${source.name} will be merged into ${target.name}.${
          this.deleteFromProduct ? " The source product will be deleted." : ""
        }`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#f4c20d",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Yes, merge products",
        cancelButtonText: "Cancel",
      });
      if (!confirmation.value) return;

      this.loading = true;
      try {
        const result = await productService.merge({
          sourceProduct: { id: Number(source.id) },
          targetProduct: { id: Number(target.id) },
          deleteSource: this.deleteFromProduct,
        });
        if (result.status) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({ icon: "success", title: "Products merged successfully" });
          this.$emit("submit");
          this.close();
        }
      } catch (error) {
        const message =
          error?.data?.message ||
          error?.response?.data?.message ||
          "Unable to merge these products.";
        this.$swal("Merge Products", message, "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.product-dialog-card {
  border-radius: 20px;
  overflow: hidden;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
}
.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}
.dialog-subtitle {
  margin-top: 2px;
  color: #64748b;
  font-size: 13px;
}
.dialog-body {
  overflow-y: auto;
  background: #fff;
}
.dialog-actions {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}
.merge-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #f8fafc;
}
.summary-icon,
.direction-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 13px;
  background: #fff8db;
}
.summary-copy {
  flex: 1 1 auto;
}
.summary-title,
.section-title,
.selected-product-name {
  color: #0f172a;
  font-weight: 800;
}
.delete-source-check {
  flex: 0 0 auto;
}
.form-section {
  height: 100%;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #f8fafc;
}
.section-heading,
.selected-product-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.section-heading {
  margin-bottom: 16px;
}
.section-kicker,
.field-label,
.direction-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
}
.field-label {
  margin-bottom: 6px;
  letter-spacing: 0;
}
.selected-product-card {
  padding: 14px;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  background: #fff;
}
.selected-product-name {
  max-width: 65%;
  font-size: 15px;
}
.product-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}
.product-detail {
  min-width: 0;
  padding: 10px;
  border-radius: 10px;
  background: #f8fafc;
}
.product-detail span,
.product-detail strong {
  display: block;
}
.product-detail span {
  margin-bottom: 3px;
  color: #64748b;
  font-size: 11px;
}
.product-detail strong {
  overflow: hidden;
  color: #1e293b;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.selection-placeholder {
  min-height: 153px;
  padding: 45px 24px;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  color: #64748b;
  text-align: center;
}
.merge-direction {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.direction-line {
  width: 1px;
  min-height: 34px;
  background: #e2e8f0;
}
.merge-warning {
  border-radius: 12px;
}
@media (max-width: 959px) {
  .merge-summary {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .delete-source-check {
    width: 100%;
    padding-left: 54px;
  }
  .merge-direction {
    min-height: 80px;
    flex-direction: row;
  }
  .direction-line {
    width: 34px;
    min-height: 1px;
    height: 1px;
  }
  .direction-icon .v-icon {
    transform: rotate(90deg);
  }
}
@media (max-width: 600px) {
  .dialog-header {
    align-items: flex-start;
  }
  .product-detail-grid {
    grid-template-columns: 1fr;
  }
  .delete-source-check {
    padding-left: 0;
  }
}
</style>
